import { useEffect, HTMLAttributes } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { User } from "../../../model/User";
import { FeedbackEntry } from "../../feedback";
import UserUtils from "../../../utils/user";
import ConfirmModal from "../confirm-modal";
import Feedback from "../../feedback/feedback-container";
import {
  AddUsersToOrgRoleAction,
  ClearErrorAction,
  GetOrgRoleAction,
  isAddErrorAction,
  RemoveUsersFromOrgRoleAction,
} from "../../../actions/actionTypes";
import Table from "../../table";
import { TABLE_SEARCH_THRESHOLD } from "../../table/table-view";
import { ModalKeys } from "../../pages/users";
import UserStatusBadge from "../../badges/user-status-badge";
import { Form } from "react-bootstrap";
import { ClosableModalWrapperComponentProps } from "../modal";
import NavigateAfterAction from "../../navigate-after-action";
import { UserValues, UserWithStatusValues } from "../../../localization/user";
import { OrganizationRoleLabels } from "../../../localization/organization-role";
import { OrganizationRole } from "../../../model/OrganizationRole";

const resolveUserName = (
  user: User | undefined | null,
  intl: { formatMessage: (v: any, v2?: any) => string }
) =>
  UserUtils.resolveDisplayName(
    user,
    intl.formatMessage(UserValues.displayName.undefined)
  );

export interface ManageRoleMembersModalDOMProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title" | "role"> {}

export interface ManageRoleMembersModalVisibilityProps
  extends Pick<
    ClosableModalWrapperComponentProps,
    "show" | "onClose" | "onExited"
  > {
  onShowFeedback: (feedback: FeedbackEntry | FeedbackEntry[]) => void;
}
export interface ManageRoleMembersModalInputProps
  extends ManageRoleMembersModalVisibilityProps {
  roleId?: string;
}
export interface ManageRoleMembersModalStateProps {
  membersToAdd: User[];
  membersToRemove: User[];
  activeSearch?: string;
  onSetActiveSearch: (s: string) => void;
  selected: User[];
  onSetSelected: (selection: User[]) => void;
}
export interface ManageRoleMembersModalDataProps
  extends Pick<ClosableModalWrapperComponentProps, "isReady"> {
  actorId: string;
  role?: OrganizationRole | null;
  onLoadRole?: (id: string) => Promise<GetOrgRoleAction>;
  onLoadOrgadminRole: () => void;
  orgAdminRoleId?: string;

  users?: User[];
  onLoadUsers?: () => void;

  roleMembers?: string[];
  onLoadRoleMembers?: (id: string) => void;

  onAddMembers?: (
    rlId: string,
    usrs: string[]
  ) => Promise<AddUsersToOrgRoleAction>;
  onRemoveMembers?: (
    rlId: string,
    usrs: string[]
  ) => Promise<RemoveUsersFromOrgRoleAction>;
  onClearError: (errorId: string) => ClearErrorAction;
}
export interface ManageRoleMembersModalProps
  extends ManageRoleMembersModalDOMProps,
    ManageRoleMembersModalStateProps,
    ManageRoleMembersModalDataProps,
    ManageRoleMembersModalInputProps {}
//</editor-fold>

function ManageRoleMembersModal(props: ManageRoleMembersModalProps) {
  //<editor-fold desc="Local variables">
  let {
    activeSearch,
    onSetActiveSearch,
    role,
    isReady,
    roleId,
    actorId,
    onLoadRole,
    users,
    onLoadUsers,
    onAddMembers,
    onRemoveMembers,
    roleMembers,
    onLoadRoleMembers,
    show,
    onClose,
    onExited,
    onShowFeedback,
    membersToAdd,
    membersToRemove,
    onSetSelected,
    selected,
    onLoadOrgadminRole,
    orgAdminRoleId,
    onClearError,
  } = props;
  if (show && !onAddMembers) {
    throw new Error(
      "ManageRoleMembersModal: Required props missing. The onAddMembers is required"
    );
  }
  if (show && !onRemoveMembers) {
    throw new Error(
      "ManageRoleMembersModal: Required props missing. The onRemoveMembers is required"
    );
  }
  if (show && role === undefined && (!roleId || !onLoadRole)) {
    throw new Error(
      "ManageRoleMembersModal: Required props missing. The onLoadRole is required with roleId"
    );
  }
  if (show && !users && !onLoadUsers) {
    throw new Error(
      "ManageRoleMembersModal: Required props missing. Either users or onLoadUsers must be provided"
    );
  }
  if (show && !roleMembers && !onLoadRoleMembers) {
    throw new Error(
      "ManageRoleMembersModal: Required props missing. Either roleMembers or onLoadRoleMembers must be provided"
    );
  }
  // this is more like a variable than a hook
  const intl = useIntl();

  //</editor-fold>

  const roleObjId = role ? role.id : undefined;
  useEffect(() => {
    if (
      !!show &&
      !!onLoadRole &&
      !!roleId &&
      (roleObjId === undefined || (!!roleObjId && roleObjId !== roleId))
    ) {
      onLoadRole(roleId).then((res) => {
        if (!roleObjId && isAddErrorAction(res)) {
          // only clear error if no data exists, leave if previous data is still available and
          // let the api error notification handler show error
          onClearError(res.error?.errorId);
        }
      });
    }
  }, [show, onLoadRole, roleId, roleObjId, onClearError]);

  useEffect(() => {
    if (show && orgAdminRoleId === undefined && onLoadOrgadminRole) {
      onLoadOrgadminRole();
    }
  }, [show, orgAdminRoleId, onLoadOrgadminRole]);
  useEffect(() => {
    if (show && !users && onLoadUsers) {
      onLoadUsers();
    }
  }, [show, users, onLoadUsers]);

  useEffect(() => {
    if (show && !roleMembers && role && onLoadRoleMembers) {
      onLoadRoleMembers(role.id as string);
    }
  }, [show, role, roleMembers, onLoadRoleMembers]);

  return (
    <ConfirmModal
      onExited={onExited}
      isReady={isReady}
      onReloadData={() => {
        if (onLoadUsers) {
          onLoadUsers();
        }
        if (roleId && onLoadRoleMembers) {
          onLoadRoleMembers(roleId);
        }
        if (roleId && onLoadRole) {
          onLoadRole(roleId).then((res) => {
            if (!role && isAddErrorAction(res)) {
              // only clear error if no data exists, leave if previous data is still available and
              // let the api error notification handler show error
              onClearError(res.error?.errorId);
            }
          });
        }
      }}
      data-test-manage-role-members-modal={role ? role.id : true}
      title={
        role
          ? intl.formatMessage(
              {
                defaultMessage: "{name}: members",
                description: "modal heading, 'name' = name of the role",
              },
              {
                name: role.name,
              }
            )
          : intl.formatMessage({
              defaultMessage: "Role not found",
              description: "modal heading when role not found",
            })
      }
      confirmTitle={
        role
          ? intl.formatMessage(
              {
                defaultMessage: "{name}: confirm members",
                description:
                  "modal confirm action heading. 'name' = name of the role",
              },
              {
                name: role.name,
              }
            )
          : undefined
      }
      confirmContent={
        <>
          <p data-test-confirm-changes-message>
            <FormattedMessage
              defaultMessage="Are you sure you wish to apply the following changes to the role memberships?"
              description={"Confirm action message"}
            />
          </p>
          {membersToRemove.length > 0 && (
            <>
              <strong>
                <FormattedMessage
                  defaultMessage="Members to remove"
                  description={"heading for summary of members to remove"}
                />
              </strong>
              <ul>
                {membersToRemove.map((g) => (
                  <li key={"toRemove" + (g.id as string)}>
                    {resolveUserName(g, intl)}
                  </li>
                ))}
              </ul>
            </>
          )}
          {membersToAdd.length > 0 && (
            <>
              <strong>
                <FormattedMessage
                  defaultMessage="Members to add"
                  description={"heading for summary of members to add"}
                />
              </strong>
              <ul>
                {membersToAdd.map((g) => (
                  <li key={"toAdd" + (g.id as string)}>
                    {resolveUserName(g, intl)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      }
      acceptButton={{
        label: intl.formatMessage({
          defaultMessage: "Yes",
          description: "confirm action button label",
        }),
      }}
      cancelButton={{
        label: intl.formatMessage({
          defaultMessage: "No",
          description: "cancel confirmation button label",
        }),
      }}
      show={show}
      onClose={onClose}
      skipConfirm={!role}
      backdrop={
        membersToAdd.length > 0 || membersToRemove.length > 0 ? "static" : true
      }
      primaryButton={{
        label: !role
          ? intl.formatMessage({
              defaultMessage: "Close",
              description: "close button label",
            })
          : intl.formatMessage({
              defaultMessage: "Apply",
              description: "primary button label",
            }),
        disabled:
          !!role && membersToAdd.length === 0 && membersToRemove.length === 0,
        tooltip:
          !!role && membersToAdd.length === 0 && membersToRemove.length === 0
            ? intl.formatMessage({
                defaultMessage: "There are no changes to apply.",
                description: "tooltip for disabled primary button",
              })
            : undefined,
      }}
      onPrimaryAction={() => {
        if (show && role) {
          if (onAddMembers && onRemoveMembers) {
            const actions = [];
            if (membersToAdd.length) {
              actions.push(
                onAddMembers(
                  role.id as string,
                  membersToAdd.map((val) => val.id as string)
                ).catch((error) => error)
              );
            }
            if (membersToRemove.length) {
              actions.push(
                onRemoveMembers(
                  role.id as string,
                  membersToRemove.map((val) => val.id as string)
                ).catch((error) => error)
              );
            }
            if (actions.length) {
              // should aways come here
              Promise.all(actions).then(
                (res) => {
                  const feedback: FeedbackEntry[] = [];
                  let addResult;
                  let removeResult;
                  if (membersToAdd.length) {
                    addResult = res[0];
                    if (membersToRemove.length) {
                      removeResult = res[1];
                    }
                  } else if (membersToRemove.length) {
                    removeResult = res[0];
                  } else {
                    throw Error("This should never happen");
                  }
                  if (addResult && addResult.error) {
                    onClearError(addResult.error?.errorId);
                    feedback.push({
                      id: "manageRoleMembers_add",
                      msg: intl.formatMessage(
                        {
                          defaultMessage: "{name}: Adding members failed.",
                          description:
                            "failure notification. 'name' = name of the role",
                        },
                        {
                          name:
                            "<strong>" +
                            (role ? role.name : "impossible") +
                            "</strong>",
                        }
                      ),
                      type: "danger",
                    });
                  } else if (addResult) {
                    feedback.push({
                      id: "manageRoleMembers_add",
                      msg: intl.formatMessage(
                        {
                          defaultMessage:
                            "{name}: {count} {count, plural, one {member} other {members}} added.",
                          description:
                            "Success notification. 'name' = name of he role, 'count' = the amount of members added",
                        },
                        {
                          name:
                            "<strong>" +
                            (role ? role.name : "impossible") +
                            "</strong>",
                          count: membersToAdd.length,
                        }
                      ),
                      autoclose: true,
                      type: "success",
                    });
                  }
                  if (removeResult && removeResult.error) {
                    onClearError(removeResult.error?.errorId);
                    feedback.push({
                      id: "manageRoleMembers_remove",
                      msg: intl.formatMessage(
                        {
                          defaultMessage: "{name}: Removing members failed.",
                          description:
                            "failure notification. 'name' = name of the role",
                        },
                        {
                          name:
                            "<strong>" +
                            (role ? role.name : "impossible") +
                            "</strong>",
                        }
                      ),
                      type: "danger",
                    });
                  } else if (removeResult) {
                    feedback.push({
                      id: "manageRoleMembers_remove",
                      msg: intl.formatMessage(
                        {
                          defaultMessage:
                            "{name}: {count} {count, plural, one {member} other {members}} removed.",
                          description:
                            "Success notification. 'name' = name of the role, 'count' = the amount of members removed",
                        },
                        {
                          name:
                            "<strong>" +
                            (role ? role.name : "impossible") +
                            "</strong>",
                          count: membersToRemove.length,
                        }
                      ),
                      autoclose: true,
                      type: "success",
                    });
                  }
                  if (feedback.length) {
                    onShowFeedback(feedback);
                  }
                },
                (rej) => {
                  throw new Error("Should not happen");
                }
              );
            }
          }
        }
        onClose();
      }}
      secondaryButton={
        role
          ? {
              label: intl.formatMessage({
                defaultMessage: "Cancel",
                description: "secondary button label",
              }),
            }
          : undefined
      }
      onSecondaryAction={onClose}
    >
      {role && (
        <>
          <Table<User>
            disableLoadingIndicator={!isReady}
            maxRows={15}
            header={
              <Form.Label className={"mr-auto pt-1 mb-0"}>
                <FormattedMessage
                  defaultMessage="Select role members"
                  description={"field label"}
                />
              </Form.Label>
            }
            compact={true}
            data-test-select-members
            search={users && users.length > TABLE_SEARCH_THRESHOLD}
            activeSearch={activeSearch}
            onSearch={onSetActiveSearch}
            columnToggle={false}
            reset={false}
            data={users && role ? users : isReady ? [] : undefined}
            pagination={false}
            identifyingColumn={"id"}
            selection={{
              multi: true,
              selectAll: true,
              disabledFor:
                orgAdminRoleId === (role ? (role.id as string) : false) &&
                roleMembers &&
                roleMembers.includes(actorId as string)
                  ? [actorId]
                  : [],
            }}
            onSelectionChanged={onSetSelected}
            selected={selected}
            columns={[
              {
                key: "id",
                label: intl.formatMessage(OrganizationRoleLabels.id),
                isTechnical: true,
                hidden: true,
              },
              {
                key: "name",
                isDummy: true,
                label: intl.formatMessage({
                  defaultMessage: "Member",
                  description: "column heading for the members to select",
                }),
                sortable: true,
                renderer: (props: {
                  cell: any;
                  row: any;
                  rowIndex: Number;
                  rendererData: any;
                }) => {
                  const t = props.rendererData.resolveValue(props.row);
                  return (
                    <>
                      <span
                        key={"link_" + props.row.id}
                        className={"mr-2"}
                        data-test-user={props.row.id}
                      >
                        <NavigateAfterAction
                          href={"/users/" + props.row.id + "/" + ModalKeys.show}
                          action={onClose}
                        >
                          <strong className={"mr-1"}>{t[0]}</strong>
                          {t[1] && <small>{" <" + t[1] + ">"}</small>}
                        </NavigateAfterAction>
                      </span>
                      <UserStatusBadge
                        status={UserUtils.resolveUserStatus(props.row)}
                        withLabel={true}
                      />
                    </>
                  );
                },
                tipRenderer: (props: {
                  cell: any;
                  row: any;
                  rowIndex: Number;
                  rendererData: any;
                }) => (
                  <>
                    {actorId ===
                      (props.row ? (props.row.id as string) : false) &&
                    orgAdminRoleId === (role ? (role.id as string) : false) &&
                    roleMembers &&
                    roleMembers.includes(props.row.id as string) ? (
                      intl.formatMessage({
                        defaultMessage:
                          "You're not allowed to remove yourself from an admin role.",
                        description: "tooltip for disabled self removal",
                      })
                    ) : (
                      <FormattedMessage
                        defaultMessage="Click to select or deselect."
                        description="tooltip for toggling member selection"
                      />
                    )}
                  </>
                ),
                rendererData: {
                  resolveValue: (itm: any) => {
                    const status = UserUtils.resolveUserStatus(itm);
                    return [
                      resolveUserName(itm, intl),
                      itm.email,
                      status
                        ? intl.formatMessage(
                            (UserWithStatusValues.status as any)[status]
                          )
                        : status,
                    ];
                  },
                },
              },
            ]}
          />
        </>
      )}
      {isReady && !role && (
        <Feedback type={"danger"} show={true} asChild={true}>
          <p>
            <FormattedMessage
              defaultMessage="Something went wrong and the role could not be loaded. The role may have been removed or you don't have sufficient access rights."
              description="message to be shown when there is no role to display"
            />
          </p>
        </Feedback>
      )}
    </ConfirmModal>
  );
}

export default ManageRoleMembersModal;
